import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../app/providers/AuthProvider';
import { Input } from '../components/Input';
import { PasswordInput } from '../components/PasswordInput';
import { Button } from '../components/Button';
import { Logo } from '../components/Logo';
import { PasswordRequirements } from '../components/PasswordRequirements';
import { COLORS } from '../../../core/constants/colors';

export const ResetPasswordPage: React.FC = () => {
  const { confirmForgotPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    code: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setFormData(prev => ({ ...prev, email: decodeURIComponent(emailParam) }));
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password requirements
    const passwordErrors: string[] = [];
    if (formData.newPassword.length < 8) {
      passwordErrors.push('Password must be at least 8 characters');
    }
    if (!/[A-Z]/.test(formData.newPassword)) {
      passwordErrors.push('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(formData.newPassword)) {
      passwordErrors.push('Password must contain at least one lowercase letter');
    }
    if (!/[0-9]/.test(formData.newPassword)) {
      passwordErrors.push('Password must contain at least one number');
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.newPassword)) {
      passwordErrors.push('Password must contain at least one special character');
    }
    
    if (passwordErrors.length > 0) {
      setError(passwordErrors[0]);
      return;
    }

    setLoading(true);

    try {
      await confirmForgotPassword({
        email: formData.email,
        code: formData.code,
        newPassword: formData.newPassword,
      });
      navigate('/login?reset=true');
    } catch (err: any) {
      setError(err.message || 'Password reset failed. Please check your code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: COLORS.extended.cultured }}
    >
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
          <Logo />
          
          <div className="text-center mb-8">
            <h1
              className="text-3xl md:text-4xl font-bold mb-2"
              style={{ color: COLORS.primary.persianIndigo }}
            >
              Reset Password
            </h1>
            <p
              className="text-sm"
              style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}
            >
              Enter the code sent to {formData.email}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <Input
              label="Verification Code"
              type="text"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              placeholder="Enter code"
              required
            />

            <div
              className="relative"
              onMouseEnter={() => setShowPasswordRequirements(true)}
              onMouseLeave={() => setShowPasswordRequirements(false)}
            >
              <PasswordInput
                label="New Password"
                value={formData.newPassword}
                onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                placeholder="Enter your new password"
                required
              />
              <PasswordRequirements password={formData.newPassword} show={showPasswordRequirements} />
            </div>

            <PasswordInput
              label="Confirm New Password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              placeholder="Re-enter password"
              required
            />

            {error && (
              <div
                className="mb-4 p-3 rounded-lg"
                style={{ backgroundColor: COLORS.extended.redSalsa + '20' }}
              >
                <p className="text-sm" style={{ color: COLORS.extended.redSalsa }}>
                  {error}
                </p>
              </div>
            )}

            <Button type="submit" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};